import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
    type ColumnDef,
    type SortingState
} from '@tanstack/react-table';
import {
    Search,
    Plus,
    AlertTriangle,
    Wrench,
    ShieldAlert,
    Box,
    ArrowLeft,
    ArrowUpDown,
    ArrowUp,
    ArrowDown
} from 'lucide-react';
import { useEquipmentList } from '@/hooks/useEquipment';
import {
    type Equipment,
    type EquipmentStatus,
    EQUIPMENT_CATEGORIES,
    EQUIPMENT_STATUSES,
    isAtParLevel,
    isMaintenanceOverdue,
    isInspectionOverdue
} from '@/types/equipment';
import EquipmentStatusPill from '@/components/equipment/EquipmentStatusPill';
import EquipmentAlerts, {
    countEquipmentAlerts
} from '@/components/equipment/EquipmentAlerts';
import PtLogo from '@/components/PtLogo';
import { useAuth } from '@/lib/auth';

// ============================================================================
// EquipmentListRoute
// ----------------------------------------------------------------------------
// Materials manager workflow:
//
//   - Top: alert summary cards (par, maintenance, inspection counts) — single
//     click filters the table to that issue
//   - Left: filter sidebar (category chips, status chips, location chips,
//     issue toggles)
//   - Center: TanStack Table with the 8 columns we agreed on. Click a row to
//     navigate to /equipment/:id (detail route — built next sub-sprint)
//   - Top-right: search box + new equipment button
//
// All filters are client-side because the dataset is bounded (one ASC's
// catalog rarely exceeds 1000 items even at full scale). RLS already trimmed
// the rows server-side. We can switch to server-side filtering later if any
// org's catalog grows past ~5000 rows.
// ============================================================================

type IssueFilter = 'par' | 'maintenance' | 'inspection' | null;

export default function EquipmentListRoute() {
    const navigate = useNavigate();
    const { signOut, user } = useAuth();
    const { data: rows, isLoading, error, refetch } = useEquipmentList();

    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<EquipmentStatus | null>(null);
    const [manufacturerFilter, setManufacturerFilter] = useState<string | null>(null);
    const [locationFilter, setLocationFilter] = useState<string | null>(null);
    const [issueFilter, setIssueFilter] = useState<IssueFilter>(null);
    const [sorting, setSorting] = useState<SortingState>([
        { id: 'equipment_name', desc: false }
    ]);

    // Apply client-side filters. We keep the order: text search → category →
    // status → issue. The order doesn't change correctness but does change how
    // many predicates each row has to clear before being kept.
    const filtered = useMemo(() => {
        if (!rows) return [];
        const q = search.trim().toLowerCase();

        return rows.filter((r) => {
            // Hide soft-deleted rows
            if (r.is_active === false) return false;

            // Text search across name, manufacturer, model, asset tag, serial
            if (q) {
                const hay = [
                    r.equipment_name,
                    r.equipment_category,
                    r.equipment_subcategory,
                    r.manufacturer,
                    r.model_number,
                    r.serial_number,
                    r.asset_tag,
                    r.current_location,
                    r.primary_storage_location
                ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase();
                if (!hay.includes(q)) return false;
            }

            if (categoryFilter && r.equipment_category !== categoryFilter) return false;
            if (statusFilter && r.status !== statusFilter) return false;
            if (manufacturerFilter && r.manufacturer !== manufacturerFilter) return false;
            if (locationFilter && r.current_location !== locationFilter) return false;

            if (issueFilter === 'par' && !isAtParLevel(r)) return false;
            if (issueFilter === 'maintenance' && !isMaintenanceOverdue(r)) return false;
            if (issueFilter === 'inspection' && !isInspectionOverdue(r)) return false;

            return true;
        });
    }, [rows, search, categoryFilter, statusFilter, manufacturerFilter, locationFilter, issueFilter]);

    // Alert counts (across the unfiltered set, so the headers always show
    // the org-wide picture even when the user has narrowed the view).
    const alertCounts = useMemo(
        () => countEquipmentAlerts(rows ?? []),
        [rows]
    );

    // Distinct counts per facet — used to show numbers on filter chips
    const categoryCounts = useMemo(() => {
        const m: Record<string, number> = {};
        for (const r of rows ?? []) {
            if (r.is_active === false) continue;
            m[r.equipment_category] = (m[r.equipment_category] ?? 0) + 1;
        }
        return m;
    }, [rows]);

    const statusCounts = useMemo(() => {
        const m: Record<string, number> = {};
        for (const r of rows ?? []) {
            if (r.is_active === false) continue;
            m[r.status] = (m[r.status] ?? 0) + 1;
        }
        return m;
    }, [rows]);

    // Distinct manufacturers (sorted, with counts) for the dropdown
    const manufacturerOptions = useMemo(() => {
        const m: Record<string, number> = {};
        for (const r of rows ?? []) {
            if (r.is_active === false) continue;
            if (!r.manufacturer) continue;
            m[r.manufacturer] = (m[r.manufacturer] ?? 0) + 1;
        }
        return Object.entries(m)
            .map(([value, count]) => ({ value, label: value, count }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [rows]);

    // Distinct locations (sorted, with counts) for the dropdown
    const locationOptions = useMemo(() => {
        const m: Record<string, number> = {};
        for (const r of rows ?? []) {
            if (r.is_active === false) continue;
            if (!r.current_location) continue;
            m[r.current_location] = (m[r.current_location] ?? 0) + 1;
        }
        return Object.entries(m)
            .map(([value, count]) => ({ value, label: value, count }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [rows]);

    // Column definitions for TanStack Table
    const columns = useMemo<ColumnDef<Equipment>[]>(
        () => [
            {
                id: 'status',
                accessorKey: 'status',
                header: 'Status',
                cell: ({ getValue }) => (
                    <EquipmentStatusPill status={getValue() as EquipmentStatus} size="sm" />
                ),
                size: 110
            },
            {
                id: 'equipment_name',
                accessorKey: 'equipment_name',
                header: 'Equipment',
                cell: ({ row }) => (
                    <div className="min-w-0">
                        <div className="font-medium text-pt-text truncate">
                            {row.original.equipment_name}
                        </div>
                        {row.original.equipment_subcategory && (
                            <div className="text-xs text-pt-text-dim truncate">
                                {row.original.equipment_subcategory}
                            </div>
                        )}
                    </div>
                )
            },
            {
                id: 'category',
                accessorKey: 'equipment_category',
                header: 'Category',
                cell: ({ getValue }) => (
                    <span className="text-sm text-pt-text-muted">
                        {getValue() as string}
                    </span>
                ),
                size: 140
            },
            {
                id: 'mfr_model',
                header: 'Mfr / Model',
                accessorFn: (row) =>
                    [row.manufacturer, row.model_number].filter(Boolean).join(' '),
                cell: ({ row }) => (
                    <div className="min-w-0">
                        <div className="text-sm text-pt-text truncate">
                            {row.original.manufacturer || '—'}
                        </div>
                        {row.original.model_number && (
                            <div className="text-xs text-pt-text-dim font-mono truncate">
                                {row.original.model_number}
                            </div>
                        )}
                    </div>
                ),
                size: 180
            },
            {
                id: 'vendor',
                accessorKey: 'vendor',
                header: 'Vendor',
                cell: ({ getValue }) => {
                    const v = getValue() as string | null;
                    return v ? (
                        <span className="text-sm text-pt-text-muted truncate block">{v}</span>
                    ) : (
                        <span className="text-pt-text-dim">—</span>
                    );
                },
                size: 150
            },
            {
                id: 'asset_tag',
                accessorKey: 'asset_tag',
                header: 'Asset Tag',
                cell: ({ getValue }) => {
                    const v = getValue() as string | null;
                    return v ? (
                        <span className="font-mono text-xs text-pt-text-muted">{v}</span>
                    ) : (
                        <span className="text-pt-text-dim">—</span>
                    );
                },
                size: 110
            },
            {
                id: 'qty',
                header: 'On Hand / Min',
                accessorFn: (row) => row.quantity_on_hand,
                cell: ({ row }) => {
                    const r = row.original;
                    const atPar = isAtParLevel(r);
                    return (
                        <div className="flex items-baseline gap-1.5 font-mono text-sm">
                            <span className={atPar ? 'text-pt-red font-medium' : 'text-pt-text'}>
                                {r.quantity_on_hand}
                            </span>
                            <span className="text-pt-text-dim">/</span>
                            <span className="text-pt-text-dim">{r.minimum_quantity}</span>
                        </div>
                    );
                },
                size: 110
            },
            {
                id: 'location',
                accessorKey: 'current_location',
                header: 'Location',
                cell: ({ getValue }) => {
                    const v = getValue() as string | null;
                    return v ? (
                        <span className="text-sm text-pt-text-muted">{v}</span>
                    ) : (
                        <span className="text-pt-text-dim">—</span>
                    );
                },
                size: 140
            },
            {
                id: 'alerts',
                header: 'Alerts',
                cell: ({ row }) => <EquipmentAlerts equipment={row.original} />,
                enableSorting: false,
                size: 160
            }
        ],
        []
    );

    const table = useReactTable({
        data: filtered,
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel()
    });

    return (
        <div className="min-h-full">
            {/* Header */}
            <header className="border-b border-pt-border/60">
                <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between gap-6">
                    <div className="flex items-center gap-3 min-w-0">
                        <Link
                            to="/"
                            className="text-pt-text-muted hover:text-pt-gold transition-colors"
                            title="Back to dashboard"
                        >
                            <ArrowLeft size={20} />
                        </Link>
                        <PtLogo size={32} />
                        <div className="min-w-0">
                            <div className="text-base font-display font-semibold tracking-wide leading-tight">
                                Equipment <span className="text-pt-gold">Catalog</span>
                            </div>
                            <div className="text-xs text-pt-text-dim leading-tight">
                                Ambulatory Surgery Center Operations
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <span className="text-xs text-pt-text-muted hidden sm:inline">
                            {user?.email}
                        </span>
                        <button
                            onClick={signOut}
                            className="text-xs uppercase tracking-wide text-pt-text-muted hover:text-pt-gold"
                        >
                            Sign out
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-[1400px] mx-auto px-6 py-6">
                {/* Alert summary cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                    <button
                        onClick={() =>
                            setIssueFilter(issueFilter === 'par' ? null : 'par')
                        }
                        className={`p-4 border rounded-lg text-left transition-colors ${
                            issueFilter === 'par'
                                ? 'bg-pt-red/10 border-pt-red'
                                : 'bg-pt-panel border-pt-border hover:border-pt-red/40'
                        }`}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <AlertTriangle
                                    size={16}
                                    className={
                                        alertCounts.par > 0 ? 'text-pt-red' : 'text-pt-text-dim'
                                    }
                                />
                                <span className="text-xs uppercase tracking-wide text-pt-text-muted">
                                    Below par
                                </span>
                            </div>
                            <span
                                className={`text-2xl font-display font-semibold ${
                                    alertCounts.par > 0 ? 'text-pt-red' : 'text-pt-text-dim'
                                }`}
                            >
                                {alertCounts.par}
                            </span>
                        </div>
                        <div className="text-xs text-pt-text-dim mt-1">
                            Items at or below minimum quantity
                        </div>
                    </button>

                    <button
                        onClick={() =>
                            setIssueFilter(issueFilter === 'maintenance' ? null : 'maintenance')
                        }
                        className={`p-4 border rounded-lg text-left transition-colors ${
                            issueFilter === 'maintenance'
                                ? 'bg-pt-amber/10 border-pt-amber'
                                : 'bg-pt-panel border-pt-border hover:border-pt-amber/40'
                        }`}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Wrench
                                    size={16}
                                    className={
                                        alertCounts.maintenance > 0
                                            ? 'text-pt-amber'
                                            : 'text-pt-text-dim'
                                    }
                                />
                                <span className="text-xs uppercase tracking-wide text-pt-text-muted">
                                    Maintenance overdue
                                </span>
                            </div>
                            <span
                                className={`text-2xl font-display font-semibold ${
                                    alertCounts.maintenance > 0
                                        ? 'text-pt-amber'
                                        : 'text-pt-text-dim'
                                }`}
                            >
                                {alertCounts.maintenance}
                            </span>
                        </div>
                        <div className="text-xs text-pt-text-dim mt-1">
                            Items past their next maintenance date
                        </div>
                    </button>

                    <button
                        onClick={() =>
                            setIssueFilter(issueFilter === 'inspection' ? null : 'inspection')
                        }
                        className={`p-4 border rounded-lg text-left transition-colors ${
                            issueFilter === 'inspection'
                                ? 'bg-pt-orange/10 border-pt-orange'
                                : 'bg-pt-panel border-pt-border hover:border-pt-orange/40'
                        }`}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <ShieldAlert
                                    size={16}
                                    className={
                                        alertCounts.inspection > 0
                                            ? 'text-pt-orange'
                                            : 'text-pt-text-dim'
                                    }
                                />
                                <span className="text-xs uppercase tracking-wide text-pt-text-muted">
                                    Inspection overdue
                                </span>
                            </div>
                            <span
                                className={`text-2xl font-display font-semibold ${
                                    alertCounts.inspection > 0
                                        ? 'text-pt-orange'
                                        : 'text-pt-text-dim'
                                }`}
                            >
                                {alertCounts.inspection}
                            </span>
                        </div>
                        <div className="text-xs text-pt-text-dim mt-1">
                            Items past regulatory inspection date
                        </div>
                    </button>
                </div>

                {/* Search + actions row */}
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                    <div className="relative flex-1">
                        <Search
                            size={16}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-pt-text-dim"
                        />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by name, manufacturer, model, serial, asset tag…"
                            className="w-full pl-9 pr-3 py-2.5 bg-pt-dark border border-pt-input-border rounded-md text-sm text-pt-text placeholder:text-pt-text-dim focus:border-pt-gold focus:outline-none"
                        />
                    </div>
                    <button
                        onClick={() => navigate('/equipment/new')}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-pt-gold text-pt-darker text-sm font-semibold uppercase tracking-wide rounded-md shadow-pt-glow-gold hover:shadow-pt-glow-gold-strong transition-shadow whitespace-nowrap"
                    >
                        <Plus size={16} strokeWidth={2.5} />
                        Add Equipment
                    </button>
                </div>

                {/* Body: filter sidebar + table */}
                <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-6">
                    {/* Filter sidebar */}
                    <aside className="space-y-5">
                        <FilterGroup
                            title="Category"
                            options={[...EQUIPMENT_CATEGORIES]}
                            counts={categoryCounts}
                            value={categoryFilter}
                            onChange={setCategoryFilter}
                        />

                        <FilterGroup
                            title="Status"
                            options={EQUIPMENT_STATUSES.map((s) => ({
                                value: s,
                                label: s.replace(/-/g, ' ')
                            }))}
                            counts={statusCounts}
                            value={statusFilter}
                            onChange={(v) => setStatusFilter(v as EquipmentStatus | null)}
                        />

                        <FilterDropdown
                            title="Manufacturer"
                            options={manufacturerOptions}
                            value={manufacturerFilter}
                            onChange={setManufacturerFilter}
                            placeholder="All manufacturers"
                        />

                        <FilterDropdown
                            title="Location"
                            options={locationOptions}
                            value={locationFilter}
                            onChange={setLocationFilter}
                            placeholder="All locations"
                        />

                        {(categoryFilter || statusFilter || manufacturerFilter || locationFilter || issueFilter || search) && (
                            <button
                                onClick={() => {
                                    setSearch('');
                                    setCategoryFilter(null);
                                    setStatusFilter(null);
                                    setManufacturerFilter(null);
                                    setLocationFilter(null);
                                    setIssueFilter(null);
                                }}
                                className="text-xs text-pt-gold hover:underline"
                            >
                                Clear all filters
                            </button>
                        )}
                    </aside>

                    {/* Table */}
                    <section className="min-w-0">
                        {isLoading && (
                            <div className="bg-pt-panel border border-pt-border rounded-lg p-12 text-center">
                                <span className="pt-spinner inline-block mr-2" />
                                <span className="text-pt-text-muted">Loading equipment…</span>
                            </div>
                        )}

                        {error && (
                            <div className="bg-pt-red/10 border border-pt-red rounded-lg p-6">
                                <div className="text-pt-red font-medium mb-2">
                                    Could not load equipment
                                </div>
                                <div className="text-sm text-pt-text-muted mb-4">
                                    {error instanceof Error ? error.message : 'Unknown error'}
                                </div>
                                <button
                                    onClick={() => refetch()}
                                    className="px-3 py-1.5 border border-pt-red text-pt-red text-sm rounded hover:bg-pt-red/10"
                                >
                                    Retry
                                </button>
                            </div>
                        )}

                        {!isLoading && !error && rows && rows.length === 0 && (
                            <EmptyState onAdd={() => navigate('/equipment/new')} />
                        )}

                        {!isLoading && !error && rows && rows.length > 0 && filtered.length === 0 && (
                            <div className="bg-pt-panel border border-pt-border rounded-lg p-12 text-center">
                                <Box size={32} className="mx-auto text-pt-text-dim mb-3" />
                                <div className="text-pt-text font-medium mb-1">No matches</div>
                                <div className="text-sm text-pt-text-muted">
                                    No equipment matches your current filters.
                                </div>
                            </div>
                        )}

                        {!isLoading && !error && filtered.length > 0 && (
                            <div className="bg-pt-panel border border-pt-border rounded-lg overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-pt-dark border-b border-pt-border">
                                            {table.getHeaderGroups().map((hg) => (
                                                <tr key={hg.id}>
                                                    {hg.headers.map((h) => {
                                                        const canSort = h.column.getCanSort();
                                                        const sortDir = h.column.getIsSorted();
                                                        return (
                                                            <th
                                                                key={h.id}
                                                                style={{
                                                                    width: h.getSize() === 150 ? undefined : h.getSize()
                                                                }}
                                                                onClick={
                                                                    canSort
                                                                        ? h.column.getToggleSortingHandler()
                                                                        : undefined
                                                                }
                                                                className={`px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wide text-pt-text-muted ${
                                                                    canSort ? 'cursor-pointer select-none hover:text-pt-text' : ''
                                                                }`}
                                                            >
                                                                <div className="flex items-center gap-1">
                                                                    {flexRender(
                                                                        h.column.columnDef.header,
                                                                        h.getContext()
                                                                    )}
                                                                    {canSort && (
                                                                        <SortIcon dir={sortDir} />
                                                                    )}
                                                                </div>
                                                            </th>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </thead>
                                        <tbody>
                                            {table.getRowModel().rows.map((row) => (
                                                <tr
                                                    key={row.id}
                                                    onClick={() =>
                                                        navigate(`/equipment/${row.original.equipment_id}`)
                                                    }
                                                    className="border-b border-pt-border last:border-b-0 hover:bg-pt-dark/50 cursor-pointer transition-colors"
                                                >
                                                    {row.getVisibleCells().map((cell) => (
                                                        <td key={cell.id} className="px-3 py-3 align-middle">
                                                            {flexRender(
                                                                cell.column.columnDef.cell,
                                                                cell.getContext()
                                                            )}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="px-3 py-2 border-t border-pt-border bg-pt-dark text-xs text-pt-text-dim flex items-center justify-between">
                                    <span>
                                        Showing {filtered.length} of {rows?.length ?? 0} items
                                    </span>
                                </div>
                            </div>
                        )}
                    </section>
                </div>
            </main>
        </div>
    );
}

// ============================================================================
// Helper components
// ============================================================================

function SortIcon({ dir }: { dir: false | 'asc' | 'desc' }) {
    if (dir === 'asc') return <ArrowUp size={12} />;
    if (dir === 'desc') return <ArrowDown size={12} />;
    return <ArrowUpDown size={12} className="opacity-40" />;
}

interface FilterOption {
    value: string;
    label: string;
}

function FilterGroup({
    title,
    options,
    counts,
    value,
    onChange
}: {
    title: string;
    options: (string | FilterOption)[];
    counts: Record<string, number>;
    value: string | null;
    onChange: (v: string | null) => void;
}) {
    const normalized: FilterOption[] = options.map((o) =>
        typeof o === 'string' ? { value: o, label: o } : o
    );

    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-pt-text-dim font-semibold mb-2">
                {title}
            </div>
            <div className="space-y-1">
                {normalized.map((opt) => {
                    const isActive = value === opt.value;
                    const count = counts[opt.value] ?? 0;
                    return (
                        <button
                            key={opt.value}
                            onClick={() => onChange(isActive ? null : opt.value)}
                            disabled={count === 0 && !isActive}
                            className={`w-full flex items-center justify-between px-2 py-1.5 text-xs rounded transition-colors ${
                                isActive
                                    ? 'bg-pt-gold/15 text-pt-gold'
                                    : count === 0
                                    ? 'text-pt-text-dim opacity-50 cursor-not-allowed'
                                    : 'text-pt-text-muted hover:text-pt-text hover:bg-pt-dark'
                            }`}
                        >
                            <span className="capitalize truncate">{opt.label}</span>
                            <span className="font-mono ml-2 flex-shrink-0">{count}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

interface DropdownOption {
    value: string;
    label: string;
    count: number;
}

function FilterDropdown({
    title,
    options,
    value,
    onChange,
    placeholder
}: {
    title: string;
    options: DropdownOption[];
    value: string | null;
    onChange: (v: string | null) => void;
    placeholder: string;
}) {
    if (options.length === 0) return null;

    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-pt-text-dim font-semibold mb-2">
                {title}
            </div>
            <select
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                className={`w-full px-2 py-1.5 text-xs rounded bg-pt-dark border ${
                    value
                        ? 'border-pt-gold/40 text-pt-gold'
                        : 'border-pt-input-border text-pt-text-muted hover:border-pt-gold/30'
                } focus:outline-none focus:border-pt-gold transition-colors`}
            >
                <option value="">{placeholder}</option>
                {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                        {opt.label} ({opt.count})
                    </option>
                ))}
            </select>
        </div>
    );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
    return (
        <div className="bg-pt-panel border border-pt-border rounded-lg p-12 text-center">
            <Box size={48} className="mx-auto text-pt-text-dim mb-4" />
            <div className="text-pt-text font-display text-lg mb-2">
                No equipment yet
            </div>
            <div className="text-sm text-pt-text-muted mb-6 max-w-sm mx-auto">
                Your OR equipment catalog is empty. Add your first item to start tracking
                inventory, maintenance schedules, and compliance.
            </div>
            <button
                onClick={onAdd}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-pt-gold text-pt-darker text-sm font-semibold uppercase tracking-wide rounded-md shadow-pt-glow-gold hover:shadow-pt-glow-gold-strong"
            >
                <Plus size={16} strokeWidth={2.5} />
                Add first item
            </button>
        </div>
    );
}
